import React, { useContext, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CodeTypeEnum,
  CompanyCodeListNotificationEmailsSetting,
  CreateCompanyCodeListRequest,
  FeaturesEnum,
  ListCompanyCodeListItem,
  MessageResponse,
} from '@api/ringil-api.v2';
import useForm from '@hooks/useForm/useForm';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import CostCenterNotificationFields, {
  duplicateRowsError,
  emailSetsSchema,
  toRowErrors,
} from '@app/pages/customer/CompanyProfile/sidebar/CostCenterNotificationFields.tsx';

interface ComponentProps {
  data?: ListCompanyCodeListItem;
  onChanged: () => void;
}

export const useEditCompanyCatalogItemSidebar = () => {
  const [visible, setVisible] = useState(false);
  const { tr } = useTranslationLgs();
  const { createCompanyCodeList, updateCompanyCodeList } = useContext(ApiContext);

  const EditCompanyCatalogItemSidebar: React.FC<ComponentProps> = ({ data, onChanged }) => {
    const { hasFeature } = useContext(AuthenticatedUserContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const onBehalfOfEnabled = hasFeature([FeaturesEnum.RESERVATION_ON_BEHALF_OF]);
    const availableCodeTypes = Object.values(CodeTypeEnum).filter(value => onBehalfOfEnabled || value !== CodeTypeEnum.ON_BEHALF_OF);

    const duplicateLocationMessage = tr('EditCompanyCatalogItemSidebar.duplicateLocation', 'Each location can be used only once');
    const duplicateCityMessage = tr('EditCompanyCatalogItemSidebar.duplicateCityMatcher', 'Each city match can be used only once');

    const emailsSchema = emailSetsSchema(tr('validation.valid_email', 'Value has to be valid email address.'));

    const validationSchema = yup.object().shape({
      externalId: yup
        .string()
        .required(tr('EditCompanyCatalogItemSidebar.externalIdRequired', 'External ID is required'))
        .max(255, tr('EditCompanyCatalogItemSidebar.externalIdMax', 'External ID must not exceed 255 characters')),
      name: yup
        .string()
        .max(255, tr('EditCompanyCatalogItemSidebar.nameMax', 'Name must not exceed 255 characters'))
        .nullable(),
      description: yup
        .string()
        .max(512, tr('EditCompanyCatalogItemSidebar.descriptionMax', 'Description must not exceed 512 characters'))
        .nullable(),
      codeType: yup
        .mixed<CodeTypeEnum>()
        .required(tr('EditCompanyCatalogItemSidebar.codeTypeRequired', 'Code type is required')),
      notificationEmails: emailsSchema
        .test('duplicateLocation', duplicateLocationMessage, function (value: Partial<CompanyCodeListNotificationEmailsSetting>[]) {
          return duplicateRowsError(this, value, s => s.companyLocationId ?? '', 'companyLocationId', duplicateLocationMessage, s => !s.cityMatch);
        })
        .test('duplicateCityMatcher', duplicateCityMessage, function (value: Partial<CompanyCodeListNotificationEmailsSetting>[]) {
          return duplicateRowsError(this, value, s => (s.cityMatcher ?? '').trim().toLowerCase(), 'cityMatcher', duplicateCityMessage, s => !!s.cityMatch);
        }),
    });

    const form = useForm<CreateCompanyCodeListRequest>(
      validationSchema,
      data ?? {
        externalId: '',
        name: '',
        description: '',
        htmlContent: '',
        codeType: undefined,
        notificationEmails: [],
      },
      values => {
        save(values);
      },
      false,
      false,
    );

    const save = (values: CreateCompanyCodeListRequest) => {
      setBeValidationErrors([]);
      const callbacks = {
        onValidationFailed: (messages: MessageResponse[]) => setBeValidationErrors(messages),
      };
      if (data) {
        updateCompanyCodeList(
          { ...values, id: data.id },
          () => {
            setVisible(false);
            onChanged();
          },
          null,
          callbacks,
        );
      } else {
        createCompanyCodeList(
          values,
          () => {
            setVisible(false);
            onChanged();
          },
          null,
          callbacks,
        );
      }
    };

    // Notification emails (To recipients resolved on the backend) are only configured for
    // cost-center (NS) entries — see RD-2443 scope.
    const isCostCenter = (data?.codeType ?? form.values.codeType) === CodeTypeEnum.COST_CENTER;

    // The HTML content + notification e-mails UI is gated behind the CODE_LIST_NOTIFICATION_EMAILS feature.
    const codeListEmailsFlag = useSecuredFlag('code-list-notification-emails', {
      kind: 'feature',
      description: 'Code list notification e-mails',
      features: [FeaturesEnum.CODE_LIST_NOTIFICATION_EMAILS],
    });
    // Notification e-mail sets only apply to cost-center (NS) code lists.
    const costCenterFlag = useSecuredFlag('cost-center-notification-emails', {
      kind: 'visibility',
      description: 'Notification e-mails apply to cost-center (NS) code lists',
      state: [{ label: 'Code type is COST_CENTER', satisfied: isCostCenter, description: 'Notification e-mail sets apply only to cost-center code lists', byDesign: true }],
    });

    return (
      <RingilSidebar
        visible={visible}
        onBack={() => setVisible(false)}
        heading={
          tr(
            data ? 'EditCompanyCatalogItemSidebar.updateCatalogItem' : 'EditCompanyCatalogItemSidebar.createCatalogItem',
            data ? 'Update company info' : 'Create company info',
          )
        }
        onContinue={() => {
          form.validateAndSend();
        }}
      >
        <div className={'flex flex-col gap-2'}>
          <BackendValidationComponent />
          <Field
            label={tr('EditCompanyCatalogItemSidebar.externalId', 'External ID')}
            errorMessage={form.errors.externalId}
            required
          >
            <RingilInputText
              value={form.values.externalId}
              onChange={v => form.setFieldValue(form.names.externalId, v)}
              fullWidth
            />
          </Field>
          <Field
            label={tr('EditCompanyCatalogItemSidebar.name', 'Name')}
            errorMessage={form.errors.name}
          >
            <RingilInputText
              value={form.values.name}
              onChange={v => form.setFieldValue(form.names.name, v)}
              fullWidth
            />
          </Field>
          <Field
            label={tr('EditCompanyCatalogItemSidebar.description', 'Description')}
            errorMessage={form.errors.description}
          >
            <RingilInputText
              value={form.values.description}
              onChange={v => form.setFieldValue(form.names.description, v)}
              fullWidth
            />
          </Field>
          {!data && (
            <Field
              label={tr('EditCompanyCatalogItemSidebar.codeType', 'Code type')}
              errorMessage={form.errors.codeType}
              required
            >
              <Dropdown
                value={form.values.codeType}
                onChange={v => form.setFieldValue(form.names.codeType, v.value)}
                options={availableCodeTypes.map(value => ({
                  value,
                  label: tr(`CompanyProfileCatalogs.codeType.${value}`, value),
                }))}
                filter
                filterBy="name,externalId"
              />
            </Field>
          )}

          <SecuredArea id={'code-list-notification-emails-section'} label={'Code list notification e-mails'} description={'Content of the notification e-mails sent for a code list item'} visible={codeListEmailsFlag}>
          <CostCenterNotificationFields
            showHtmlContent
            htmlContent={form.values.htmlContent}
            notificationEmails={form.values.notificationEmails}
            onChangeHtmlContent={v => form.setFieldValue(form.names.htmlContent, v)}
            onChangeNotificationEmails={v => form.setFieldValue(form.names.notificationEmails, v)}
          />

          <SecuredArea id={'cost-center-notification-emails'} label={'Cost center notification e-mails'} description={'Notification e-mail addresses of a cost-center code list item, set per location and city'} visible={costCenterFlag}>
            <CostCenterNotificationFields
              showLocationSets
              showCitySets
              rowErrors={toRowErrors(form.errors.notificationEmails)}
              htmlContent={form.values.htmlContent}
              notificationEmails={form.values.notificationEmails}
              onChangeHtmlContent={v => form.setFieldValue(form.names.htmlContent, v)}
              onChangeNotificationEmails={v => form.setFieldValue(form.names.notificationEmails, v)}
            />
          </SecuredArea>
          </SecuredArea>

        </div>
      </RingilSidebar>
    );
  };

  return { setVisible, EditCompanyCatalogItemSidebar };
};
